import React, { FC, memo } from 'react';
import styled, { css } from 'styled-components';
import { Dropdown as DropdownReactstrap, DropdownMenu } from 'reactstrap';
import Icon from '@minecraft.icon';
import { DropdownWithChildrenProps, DropdownTogglerTypeEnum, SelectionsResultTypeEnum } from './shared/types';
import DropdownWithChildrenProvider, { useDropdownWithChildrenContext } from './shared/context';
import DropdownWithChildrenToggler from './DropdownWithChildrenToggler';
import DropdownListWithSearch from './DropdownWithChildrenDropdownListWithSearch';
import DropdownList from './DropdownWithChildrenDropdownList';

const StyledDropdownMenu = styled(DropdownMenu)<{
  $isMultipleSelect: boolean;
  $isFullWidth: boolean;
  $withChildren: boolean;
}>`
  background-color: ${(props): string => props.theme.form.backgroundColor};
  border: 0.0625rem solid ${(props): string => props.theme.form.borderColor};
  border-radius: 0.25rem;
  color: ${(props): string => props.theme.typography.body.color};
  padding: 0.5rem 0.5rem 0.625rem 0.5rem;
  min-width: unset;

  ${(props) => {
    if (props.$isFullWidth) {
      return `width: 100%;`;
    }

    return css`
      min-width: ${props.$withChildren ? '16rem' : '15rem'};
      max-width: ${props.$withChildren ? '16rem' : '15rem'};
    `;
  }}
`;

const StyledDropdownMenuHead = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
`;

const StyledDropdownMenuBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledCloseIcon = styled(Icon)`
  cursor: pointer;
  height: 1rem;
  width: 1rem;
`;

const DropdownWithChildren: FC<DropdownWithChildrenProps> = ({
  withSearch,
  withSelectAll,
  withAutocomplete,
  withSelectionsResult,
  withSeeMoreButton = true,
  showDropdownCloseButton = true,
  heightDropdownAuto = false,
  isFullWidth: isFullWidthProp,
  isLoading,
  isMultipleSelect,
  isDisabled,
  isClearable = true,
  isCollapsable = true,
  isChildrensContentFilled = false,
  id,
  placeholder,
  dropdownPositionFixed = true,
  dropdownDirection,
  dropdownTogglerType,
  selectionsResultType = SelectionsResultTypeEnum.jsxElement,
  selectionsLimit,
  initialOffset,
  initialLimit,
  optionsTotalCount,
  options,
  onOptionsFetch,
  onOptionsFetchMore,
  toggleLabel,
  dataTestId = 'dropdown-with-children',
  children,
  qaId,
  className,
  onBlur,
  textWrapChildrenToggler,
}) => {
  const { isDropdownOpen, toggleDropdown } = useDropdownWithChildrenContext();

  const isFullWidth = isFullWidthProp || dropdownTogglerType === DropdownTogglerTypeEnum.input;
  const withChildren = Boolean(children);

  return (
    <DropdownReactstrap
      isOpen={isDropdownOpen}
      disabled={isDisabled}
      direction={dropdownDirection}
      toggle={toggleDropdown}
      className={className}
      onBlur={onBlur}
    >
      <DropdownWithChildrenToggler
        withSelectionsResult={withSelectionsResult}
        isDisabled={isDisabled}
        isChildrensContentFilled={isChildrensContentFilled}
        isClearable={isClearable}
        id={id}
        placeholder={placeholder}
        dropdownTogglerType={dropdownTogglerType}
        selectionsResultType={selectionsResultType}
        toggleLabel={toggleLabel}
        onOptionsFetch={withChildren ? onOptionsFetch : undefined}
        dataTestId={dataTestId}
        qaId={qaId}
        textWrap={textWrapChildrenToggler}
      />
      <StyledDropdownMenu
        $isMultipleSelect={isMultipleSelect}
        $isFullWidth={isFullWidth}
        $withChildren={withChildren}
        positionFixed={!isFullWidth && dropdownPositionFixed}
        data-qa-id={toggleLabel}
        data-testid={qaId}
        aria-label={`${toggleLabel} Menu`}
      >
        {showDropdownCloseButton && dropdownTogglerType === DropdownTogglerTypeEnum.button && (
          <StyledDropdownMenuHead>
            <StyledCloseIcon name="close" inheritColor onClick={toggleDropdown} />
          </StyledDropdownMenuHead>
        )}
        <StyledDropdownMenuBody data-testid={`${dataTestId}-dd-menu-body`}>
          {children}
          {withSearch && !children && (
            <DropdownListWithSearch
              withSelectAll={withSelectAll}
              withAutocomplete={withAutocomplete}
              withSeeMoreButton={withSeeMoreButton}
              heightDropdownAuto={heightDropdownAuto}
              isLoading={isLoading}
              isMultipleSelect={isMultipleSelect}
              isCollapsable={isCollapsable}
              dropdownTogglerType={dropdownTogglerType}
              selectionsLimit={selectionsLimit}
              initialOffset={initialOffset}
              initialLimit={initialLimit}
              optionsTotalCount={optionsTotalCount}
              options={options}
              onOptionsFetch={onOptionsFetch}
              onOptionsFetchMore={onOptionsFetchMore}
              dataTestId={dataTestId}
            />
          )}
          {!withSearch && !children && (
            <DropdownList
              withSelectAll={withSelectAll}
              withAutocomplete={withAutocomplete}
              heightDropdownAuto={heightDropdownAuto}
              isLoading={isLoading}
              isMultipleSelect={isMultipleSelect}
              isCollapsable={isCollapsable}
              selectionsLimit={selectionsLimit}
              initialOffset={initialOffset}
              initialLimit={initialLimit}
              options={options}
              onOptionsFetch={onOptionsFetch}
              dataTestId={dataTestId}
            />
          )}
        </StyledDropdownMenuBody>
      </StyledDropdownMenu>
    </DropdownReactstrap>
  );
};

const DropdownWithChildrenContainer: FC<DropdownWithChildrenProps> = ({
  withFormik,
  withSelectionsResult = false,
  isMultipleSelect = true,
  dropdownTogglerType = DropdownTogglerTypeEnum.button,
  name,
  maxSelectionsResultLength,
  options,
  initialSelectedOptions = [],
  initialDisabledOptions = [],
  onOptionsChange,
  ...restProps
}) => {
  return (
    <DropdownWithChildrenProvider
      withFormik={withFormik}
      withSelectionsResult={withSelectionsResult}
      isMultipleSelect={isMultipleSelect}
      dropdownTogglerType={dropdownTogglerType}
      name={name}
      maxSelectionsResultLength={maxSelectionsResultLength}
      options={options}
      initialSelectedOptions={initialSelectedOptions}
      initialDisabledOptions={initialDisabledOptions}
      onOptionsChange={onOptionsChange}
    >
      <DropdownWithChildren
        withSelectionsResult={withSelectionsResult}
        isMultipleSelect={isMultipleSelect}
        dropdownTogglerType={dropdownTogglerType}
        options={options}
        dataTestId={restProps?.dataTestId || name}
        {...restProps}
      />
    </DropdownWithChildrenProvider>
  );
};

export default memo(DropdownWithChildrenContainer);
